"use client";

import { useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  Check,
  ClipboardCopy,
  Database,
  FileInput,
  FileOutput,
  LoaderCircle,
  RefreshCw,
  Save,
  ShieldCheck,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

const READ_OPERATIONS = [
  "ORIGINALITY_CONTEXT",
  "LATEST_APPROVED",
  "NEAREST_MATCHES",
  "MOVIE_FINGERPRINT",
  "OVERUSE_HISTORY",
  "ENDING_FAMILIES",
  "SESSION_MEMORY",
] as const;

const WRITE_OPERATIONS = [
  "APPEND_MEMORY",
  "UPDATE_MEMORY",
  "RECORD_DECISION",
  "SAVE_FINGERPRINT_DRAFT",
] as const;

const MEMORY_TYPES = [
  "CONCEPT_BRAINSTORM",
  "MOVIE_FINGERPRINT",
  "CHARACTER_MEMORY",
  "RELATIONSHIP_MEMORY",
  "LOCATION_MEMORY",
  "CONTINUITY_LOCK",
  "STAGE_ARTIFACT",
  "ORIGINALITY_AUDIT",
  "HUMAN_DECISION",
  "SCREENPLAY_PART",
] as const;

type JsonRecord = Record<string, unknown>;

function parseJsonCode(value: string): JsonRecord {
  const clean = value.trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/, "");
  const parsed: unknown = JSON.parse(clean);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("The code must be one JSON object.");
  }
  return parsed as JsonRecord;
}

function makeSessionId(): string {
  const random = crypto.randomUUID().replace(/-/g, "").slice(0, 12).toUpperCase();
  return `RUNYI-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}-${random}`;
}

function createContextCode(sessionId: string, runId: string | null): string {
  return JSON.stringify({
    protocol: "RUNYI_DB_BRIDGE_CONTEXT_V1",
    exchange_session_id: sessionId,
    concept_run_id: runId,
    database_access: "MANUAL_COPY_BRIDGE_ONLY",
    read_operations: READ_OPERATIONS,
    write_operations: WRITE_OPERATIONS,
    memory_types: MEMORY_TYPES,
    rules: [
      "When database evidence is needed, return only one RUNYI_DB_REQUEST_V1 JSON object.",
      "Never invent database results; wait for the RUNYI_DB_RESULT_V1 code.",
      "When memory should be saved, return only one RUNYI_MEMORY_WRITE_V1 JSON object.",
      "Use incremental summaries or numbered screenplay parts instead of the entire project.",
      "Do not include SQL, API keys, passwords, or delete instructions.",
      "No RAF number may be assigned before human approval at canonical Stage 22 / supporting Step 34.",
    ],
    request_example: {
      protocol: "RUNYI_DB_REQUEST_V1",
      exchange_session_id: sessionId,
      operation: "ORIGINALITY_CONTEXT",
      query_text: "Paste the candidate logline or fingerprint search description here",
      limit: 5,
    },
    memory_example: {
      protocol: "RUNYI_MEMORY_WRITE_V1",
      exchange_session_id: sessionId,
      concept_run_id: runId,
      operation: "APPEND_MEMORY",
      memory_type: "CONCEPT_BRAINSTORM",
      canonical_stage: "5T/5O",
      part_number: 1,
      summary: "Concise description of the new approved memory",
      payload: { decisions: [], continuity_locks: [], unresolved_questions: [] },
    },
  }, null, 2);
}

export function DatabaseBridge({
  supabase,
  connected,
  currentRunId,
}: {
  supabase: SupabaseClient | null;
  connected: boolean;
  currentRunId: string | null;
}) {
  const [sessionId, setSessionId] = useState("");
  const [requestCode, setRequestCode] = useState("");
  const [resultCode, setResultCode] = useState("");
  const [memoryCode, setMemoryCode] = useState("");
  const [memoryPreview, setMemoryPreview] = useState<JsonRecord | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<"read" | "write" | null>(null);
  const [copied, setCopied] = useState<"instructions" | "result" | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const stored = window.localStorage.getItem("runyi:bridge-session");
      const next = stored || makeSessionId();
      window.localStorage.setItem("runyi:bridge-session", next);
      setSessionId(next);
      setResultCode(createContextCode(next, currentRunId));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [currentRunId]);

  const bridgeInstructions = useMemo(() => sessionId ? `You are working with the RUNYI AWARA FILMS manual database bridge.\n\n${createContextCode(sessionId, currentRunId)}\n\nFollow the protocol exactly. When you need catalogue evidence, output only a RUNYI_DB_REQUEST_V1 JSON object. After I return a RUNYI_DB_RESULT_V1 object, use only that evidence. When memory needs to be stored, output only a RUNYI_MEMORY_WRITE_V1 object for human review. Never output SQL or claim direct database access.` : "", [currentRunId, sessionId]);

  function newSession() {
    const next = makeSessionId();
    window.localStorage.setItem("runyi:bridge-session", next);
    setSessionId(next);
    setRequestCode("");
    setMemoryCode("");
    setMemoryPreview(null);
    setResultCode(createContextCode(next, currentRunId));
    setNotice("New exchange session created. Copy the context code back to ChatGPT.");
  }

  async function copyText(value: string, target: "instructions" | "result") {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(target);
      setNotice(target === "instructions" ? "Bridge instructions copied to your clipboard." : "Database result code copied. Paste it back into ChatGPT.");
      window.setTimeout(() => setCopied(null), 1800);
    } catch {
      setNotice("Press and hold inside the box, then choose Copy.");
    }
  }

  function validateRequest(code: JsonRecord): JsonRecord {
    if (code.protocol !== "RUNYI_DB_REQUEST_V1") throw new Error("Expected protocol RUNYI_DB_REQUEST_V1.");
    if (code.exchange_session_id && code.exchange_session_id !== sessionId) throw new Error("This request belongs to a different exchange session.");
    const operation = String(code.operation ?? "").toUpperCase();
    if (!(READ_OPERATIONS as readonly string[]).includes(operation)) throw new Error("That read operation is not allowed.");
    if (["ORIGINALITY_CONTEXT", "NEAREST_MATCHES"].includes(operation) && String(code.query_text ?? "").trim().length < 8) {
      throw new Error("This request needs a candidate logline or fingerprint description.");
    }
    const limit = Math.max(3, Math.min(10, Number(code.limit) || 5));
    return { ...code, protocol: "RUNYI_DB_REQUEST_V1", exchange_session_id: sessionId, concept_run_id: currentRunId, operation, limit };
  }

  async function runReadRequest() {
    setNotice(null);
    if (!supabase || !connected) {
      setNotice("Sign in to the database in Settings before running a request code.");
      return;
    }
    let request: JsonRecord;
    try {
      request = validateRequest(parseJsonCode(requestCode));
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "The database request code is invalid.");
      return;
    }
    setBusy("read");
    const { data, error } = await supabase.rpc("runyi_execute_bridge_read", { p_request: request });
    setBusy(null);
    if (error) {
      const needsUpgrade = error.code === "PGRST202" || /runyi_execute_bridge_read|schema cache/i.test(error.message);
      setNotice(needsUpgrade ? "Install database bridge upgrade 08 before running request codes." : error.message);
      return;
    }
    setResultCode(JSON.stringify({
      protocol: "RUNYI_DB_RESULT_V1",
      exchange_session_id: sessionId,
      concept_run_id: currentRunId,
      generated_at: new Date().toISOString(),
      request,
      database_result: data,
      instruction: "Use this evidence in the current brainstorming turn. Do not invent records beyond it.",
    }, null, 2));
    setNotice("Database evidence retrieved. Copy the output code back into ChatGPT.");
  }

  function previewMemoryUpdate() {
    setNotice(null);
    try {
      const update = parseJsonCode(memoryCode);
      if (update.protocol !== "RUNYI_MEMORY_WRITE_V1") throw new Error("Expected protocol RUNYI_MEMORY_WRITE_V1.");
      if (update.exchange_session_id && update.exchange_session_id !== sessionId) throw new Error("This update belongs to a different exchange session.");
      const operation = String(update.operation ?? "").toUpperCase();
      const memoryType = String(update.memory_type ?? "").toUpperCase();
      if (!(WRITE_OPERATIONS as readonly string[]).includes(operation)) throw new Error("That memory operation is not allowed.");
      if (!(MEMORY_TYPES as readonly string[]).includes(memoryType)) throw new Error("That memory type is not allowed.");
      if (String(update.summary ?? "").trim().length < 12) throw new Error("The update needs a clear summary.");
      if (!update.payload || typeof update.payload !== "object" || Array.isArray(update.payload)) throw new Error("The update needs one structured payload object.");
      const suppliedRun = update.concept_run_id ? String(update.concept_run_id) : null;
      if (suppliedRun && currentRunId && suppliedRun !== currentRunId) throw new Error("The update names a different concept run. Start a new bridge session before switching movies.");
      setMemoryPreview({
        ...update,
        protocol: "RUNYI_MEMORY_WRITE_V1",
        exchange_session_id: sessionId,
        concept_run_id: suppliedRun || currentRunId,
        operation,
        memory_type: memoryType,
        part_number: Math.max(1, Number(update.part_number) || 1),
      });
      setNotice("Review the preview below. Nothing has been written yet.");
    } catch (error) {
      setMemoryPreview(null);
      setNotice(error instanceof Error ? error.message : "The memory update code is invalid.");
    }
  }

  async function confirmMemoryUpdate() {
    if (!supabase || !connected || !memoryPreview) {
      setNotice("Sign in and preview a valid memory update first.");
      return;
    }
    setBusy("write");
    const { data, error } = await supabase.functions.invoke("runyi-database-bridge", {
      body: {
        operation: "apply_memory_update",
        update: memoryPreview,
        human_confirmed: true,
      },
    });
    setBusy(null);
    const errorMessage = error?.message ?? (data?.error ? String(data.error) : null);
    if (errorMessage) {
      const needsUpgrade = /runyi_service_apply_bridge_memory_update|runyi-database-bridge|schema cache/i.test(errorMessage);
      setNotice(needsUpgrade ? "Install the current RUNYI database-bridge upgrade before saving memory codes." : errorMessage);
      return;
    }
    setResultCode(JSON.stringify({
      protocol: "RUNYI_MEMORY_ACK_V1",
      exchange_session_id: sessionId,
      concept_run_id: memoryPreview.concept_run_id ?? null,
      saved_at: new Date().toISOString(),
      database_acknowledgement: data?.acknowledgement ?? data,
      instruction: "The reviewed memory update was saved. Continue from this confirmed revision.",
    }, null, 2));
    setMemoryCode("");
    setMemoryPreview(null);
    setNotice("Memory update saved. Copy the acknowledgement code back into ChatGPT.");
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">Manual database bridge</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">ChatGPT ↔ fingerprint memory</h1>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-foreground/52">Brainstorm in ChatGPT with your ZIP files. Use these codes only when ChatGPT needs catalogue evidence or when reviewed memory must be recorded.</p>
        </div>
        <Button variant="outline" onClick={newSession} className="h-11 w-fit border-foreground/12 bg-foreground/5 text-foreground hover:bg-foreground/10 hover:text-foreground"><RefreshCw />New session</Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge variant="outline" className="border-emerald-400/25 bg-emerald-400/8 text-emerald-300">No API cost</Badge>
        <Badge variant="outline" className="border-foreground/12 text-foreground/55">Session {sessionId || "Preparing…"}</Badge>
        <Badge variant="outline" className="border-foreground/12 text-foreground/55">{currentRunId ? `Run ${currentRunId}` : "Pre-run brainstorming"}</Badge>
      </div>

      <Card className="border-foreground/10 bg-card py-0">
        <CardContent className="space-y-4 p-5 sm:p-7">
          <div className="flex items-start gap-3"><div className="grid size-10 shrink-0 place-items-center rounded-xl bg-sky-400/10 text-sky-300"><FileInput className="size-5" /></div><div><p className="font-semibold">1 · Database request code</p><p className="mt-1 text-sm leading-6 text-foreground/48">Paste the read-only RUNYI_DB_REQUEST_V1 code supplied by ChatGPT.</p></div></div>
          <Textarea value={requestCode} onChange={(event) => setRequestCode(event.target.value)} placeholder={'{"protocol":"RUNYI_DB_REQUEST_V1","operation":"ORIGINALITY_CONTEXT","query_text":"…","limit":5}'} className="min-h-40 border-foreground/12 bg-[var(--surface-inset)] font-mono text-[14px] leading-6 text-foreground placeholder:text-foreground/24" />
          <div className="grid gap-2 sm:grid-cols-2">
            <Button variant="outline" onClick={() => copyText(bridgeInstructions, "instructions")} disabled={!sessionId} className="h-11 border-foreground/12 bg-foreground/5 text-foreground hover:bg-foreground/10 hover:text-foreground">{copied === "instructions" ? <Check /> : <ClipboardCopy />}{copied === "instructions" ? "Copied" : "Copy bridge instructions"}</Button>
            <Button onClick={runReadRequest} disabled={busy !== null || !requestCode.trim()} className="h-11 bg-primary font-bold text-primary-foreground hover:bg-primary/90">{busy === "read" ? <LoaderCircle className="animate-spin" /> : <Database />}Run safe database read</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-primary/20 bg-gradient-to-br from-card to-background py-0">
        <CardContent className="space-y-4 p-5 sm:p-7">
          <div className="flex items-start gap-3"><div className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><FileOutput className="size-5" /></div><div><p className="font-semibold">2 · Database output code</p><p className="mt-1 text-sm leading-6 text-foreground/48">Copy this context, database result, or save acknowledgement back into ChatGPT.</p></div></div>
          <Textarea readOnly value={resultCode} className="min-h-52 border-foreground/10 bg-background font-mono text-[14px] leading-6 text-foreground/72" />
          <div className="grid gap-2 sm:grid-cols-2">
            <Button variant="outline" onClick={() => setResultCode(createContextCode(sessionId, currentRunId))} disabled={!sessionId} className="h-11 border-foreground/12 bg-foreground/5 text-foreground hover:bg-foreground/10 hover:text-foreground"><RefreshCw />Show context code</Button>
            <Button onClick={() => copyText(resultCode, "result")} disabled={!resultCode} className="h-11 bg-primary font-bold text-primary-foreground hover:bg-primary/90">{copied === "result" ? <Check /> : <ClipboardCopy />}{copied === "result" ? "Copied" : "Copy output to ChatGPT"}</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-emerald-400/16 bg-card py-0">
        <CardContent className="space-y-4 p-5 sm:p-7">
          <div className="flex items-start gap-3"><div className="grid size-10 shrink-0 place-items-center rounded-xl bg-emerald-400/10 text-emerald-300"><Save className="size-5" /></div><div><p className="font-semibold">3 · Database memory update code</p><p className="mt-1 text-sm leading-6 text-foreground/48">Paste a RUNYI_MEMORY_WRITE_V1 code. You will see a preview before any record is written.</p></div></div>
          <Textarea value={memoryCode} onChange={(event) => { setMemoryCode(event.target.value); setMemoryPreview(null); }} placeholder={'{"protocol":"RUNYI_MEMORY_WRITE_V1","operation":"APPEND_MEMORY","memory_type":"MOVIE_FINGERPRINT","summary":"…","payload":{}}'} className="min-h-44 border-foreground/12 bg-[var(--surface-inset)] font-mono text-[14px] leading-6 text-foreground placeholder:text-foreground/24" />
          <Button variant="outline" onClick={previewMemoryUpdate} disabled={!memoryCode.trim() || busy !== null} className="h-11 w-full border-foreground/12 bg-foreground/5 text-foreground hover:bg-foreground/10 hover:text-foreground"><ShieldCheck />Validate and preview update</Button>

          {memoryPreview && (
            <div className="space-y-4 rounded-xl border border-emerald-400/20 bg-emerald-400/[0.055] p-4">
              <div className="flex items-center justify-between gap-3"><p className="font-semibold text-emerald-200">Human review required</p><Badge className="bg-emerald-400/10 text-emerald-300">Not saved</Badge></div>
              <dl className="grid gap-3 text-sm sm:grid-cols-2">
                <PreviewRow label="Operation" value={String(memoryPreview.operation)} />
                <PreviewRow label="Memory type" value={String(memoryPreview.memory_type)} />
                <PreviewRow label="Canonical stage" value={String(memoryPreview.canonical_stage ?? "Unspecified")} />
                <PreviewRow label="Part" value={String(memoryPreview.part_number)} />
              </dl>
              <div><p className="text-xs uppercase tracking-wider text-foreground/38">Summary</p><p className="mt-1 text-sm leading-6 text-foreground/72">{String(memoryPreview.summary)}</p></div>
              <p className="text-xs leading-5 text-foreground/42">This creates a new auditable revision. It cannot delete records or assign an RAF number.</p>
              <Button onClick={confirmMemoryUpdate} disabled={busy !== null} className="h-12 w-full bg-emerald-400 font-bold text-[#07130e] hover:bg-emerald-300">{busy === "write" ? <LoaderCircle className="animate-spin" /> : <Check />}Confirm database update</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {notice && <div className="rounded-xl border border-amber-300/20 bg-amber-300/8 p-4 text-sm leading-6 text-amber-50">{notice}</div>}

      <div className="rounded-xl border border-foreground/8 bg-foreground/[0.035] p-4 text-xs leading-5 text-foreground/42"><span className="font-semibold text-foreground/65">Safety boundary:</span> Request codes are read-only. Memory updates append a reviewed revision. Raw SQL, deletes, secrets, and automatic RAF assignment are rejected.</div>
    </div>
  );
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return <div><dt className="text-xs uppercase tracking-wider text-foreground/38">{label}</dt><dd className="mt-1 break-words text-foreground/72">{value}</dd></div>;
}
